import { FC, useState, useMemo, useCallback, lazy, Suspense } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import LeopartLoader from '../../../shared/LeopartLoader/LeopartLoader';
import GenericModalV2 from '../../../shared/GenericModal/GenericModal';
import {
  Plus,
  Pencil,
  Copy,
  Trash2,
  Search,
  Clock,
  ChevronLeft,
  ChevronRight,
  Box,
} from 'lucide-react';
import { useEndpoints } from '../../../hooks/useEndpoints';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import { useConfirmModal } from '../../../hooks/useConfirmModal/useConfirmModal';
import { IFieldBlock, TacticalPhase } from '../../../models/field-programming';
import { TacticalSchemaSnapshot } from '../../../models/tactical-schema';
import FieldBlockBuilderModal from '../FieldBlockBuilderModal/FieldBlockBuilderModal';
import styles from './FieldBlocksTab.module.scss';

const TacticalBoardViewer = lazy(() => import('../TacticalBoard/TacticalBoardViewer'));

// NOTE: 4 dei valori enum (activation/athletic/technical/compensative) non hanno
// ancora label/colore definiti nel dominio — accesso reso difensivo via Partial.
const PHASE_LABELS: Partial<Record<TacticalPhase, string>> = {
  warmup: 'Riscaldamento',
  situational: 'Situazionale',
  match: 'Partita',
  cool_down: 'Defaticamento',
};

const PHASE_COLORS: Partial<Record<TacticalPhase, string>> = {
  warmup: '#f59e0b',
  situational: '#6366f1',
  match: '#10b981',
  cool_down: '#0ea5e9',
};

const BLOCK_TYPE_LABELS: Record<string, string> = {
  sequential: 'Sequenziale',
  parallel: 'Parallelo',
  rotation: 'Rotazione',
  break: 'Pausa',
  physical: 'Fisico',
};

const MAX_PREVIEW_EXERCISES = 3;

const FieldBlocksTab: FC = () => {
  const api = useEndpoints();
  const qc = useQueryClient();
  const { handleOpen: openConfirm } = useConfirmModal();
  const [search, setSearch] = useState('');
  const [phaseFilter, setPhaseFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingBlock, setEditingBlock] = useState<IFieldBlock | null>(null);
  const [previewOpen, setPreviewOpen] = useState<{
    snapshot: TacticalSchemaSnapshot;
    blockName: string;
  } | null>(null);
  const debouncedSearch = useDebouncedValue(search, 300);

  const blocksQ = useQuery({
    queryKey: ['fieldProgramming', 'blocks', { search: debouncedSearch, phaseFilter, page }],
    queryFn: () =>
      api.fieldProgramming.blocks
        .list({
          search: debouncedSearch || undefined,
          tacticalPhase: phaseFilter || undefined,
          page,
          limit: 20,
        })
        .then((r) => r.data),
    staleTime: 2 * 60 * 1000,
  });

  const dupMut = useMutation({
    mutationFn: (id: string) => api.fieldProgramming.blocks.duplicate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fieldProgramming', 'blocks'] }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.fieldProgramming.blocks.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fieldProgramming', 'blocks'] }),
  });

  const blocks = useMemo(() => blocksQ.data?.docs ?? [], [blocksQ.data]);
  const totalPages = blocksQ.data?.totalPages ?? 1;
  const totalDocs = blocksQ.data?.totalDocs ?? 0;

  const handleNew = useCallback(() => {
    setEditingBlock(null);
    setBuilderOpen(true);
  }, []);

  const handleEdit = useCallback((block: IFieldBlock) => {
    setEditingBlock(block);
    setBuilderOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setBuilderOpen(false);
    setEditingBlock(null);
  }, []);

  const handleDelete = useCallback(
    (block: IFieldBlock) => {
      openConfirm({
        title: 'Eliminare blocco?',
        description: `Stai per eliminare "${block.name}". L'azione non è reversibile.`,
        onConfirm: () => deleteMut.mutateAsync(block._id),
      });
    },
    [openConfirm, deleteMut],
  );

  return (
    <div className={styles.container}>
      {/* Filter bar */}
      <div className={styles.filterBar}>
        <div className={styles.filterPills}>
          <button
            className={`${styles.filterPill} ${phaseFilter === '' ? styles.filterPillActive : ''}`}
            onClick={() => {
              setPhaseFilter('');
              setPage(1);
            }}
          >
            Tutti
            {totalDocs > 0 && <span className={styles.pillCount}>{totalDocs}</span>}
          </button>
          {(Object.keys(PHASE_LABELS) as TacticalPhase[]).map((p) => (
            <button
              key={p}
              className={`${styles.filterPill} ${phaseFilter === p ? styles.filterPillActive : ''}`}
              onClick={() => {
                setPhaseFilter(p);
                setPage(1);
              }}
            >
              <span
                className={styles.pillDot}
                style={{ background: phaseFilter === p ? '#fff' : PHASE_COLORS[p] }}
              />
              {PHASE_LABELS[p]}
            </button>
          ))}
        </div>
        <button className={styles.addBtn} onClick={handleNew}>
          <Plus size={15} strokeWidth={2.5} />
          Nuovo Blocco
        </button>
      </div>

      {/* Search */}
      <div className={styles.searchWrapper}>
        <Search className={styles.searchIcon} />
        <input
          type="text"
          placeholder="Cerca blocco per nome..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className={styles.searchInput}
        />
      </div>

      {/* Content */}
      {blocksQ.isLoading ? (
        <div className={styles.loadingState}>
          <LeopartLoader />
        </div>
      ) : blocks.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIconWrap}>
            <Box size={24} strokeWidth={1.5} />
          </div>
          <p className={styles.emptyTitle}>Nessun blocco trovato</p>
          <p className={styles.emptySubtitle}>
            {search || phaseFilter
              ? 'Prova a modificare i filtri di ricerca'
              : 'Crea il tuo primo blocco campo per iniziare'}
          </p>
        </div>
      ) : (
        <>
          <div className={styles.grid}>
            {blocks.map((block) => {
              const phaseColor = PHASE_COLORS[block.tacticalPhase] || '#757575';
              const exercises = block.exercises ?? [];
              const previewExercises = exercises.slice(0, MAX_PREVIEW_EXERCISES);
              const remaining = exercises.length - MAX_PREVIEW_EXERCISES;

              return (
                <div key={block._id} className={styles.card}>
                  <div className={styles.cardColorStrip} style={{ background: phaseColor }} />
                  <div className={styles.cardBody}>
                    {/* Header */}
                    <div className={styles.cardHeader}>
                      <div className={styles.cardTitleArea}>
                        <p className={styles.blockTitle}>{block.name}</p>
                        <div className={styles.cardBadges}>
                          <span
                            className={styles.typeBadge}
                            style={{
                              background: phaseColor + '18',
                              color: phaseColor,
                              border: `1px solid ${phaseColor}30`,
                            }}
                          >
                            {PHASE_LABELS[block.tacticalPhase]}
                          </span>
                          <span className={styles.subTypeBadge}>
                            {BLOCK_TYPE_LABELS[block.blockType] ?? block.blockType}
                          </span>
                        </div>
                      </div>
                      <div className={styles.cardActions}>
                        <button
                          className={styles.actionIconBtn}
                          onClick={() => handleEdit(block)}
                          title="Modifica"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          className={styles.actionIconBtn}
                          onClick={() => dupMut.mutate(block._id)}
                          title="Duplica"
                        >
                          <Copy size={14} />
                        </button>
                        <button
                          className={`${styles.actionIconBtn} ${styles.actionIconBtnDanger}`}
                          onClick={() => handleDelete(block)}
                          title="Elimina"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>

                    {/* Description */}
                    {block.description && (
                      <p className={styles.description}>{block.description}</p>
                    )}

                    {/* Exercise preview */}
                    {previewExercises.length > 0 && (
                      <div className={styles.exercisePreview}>
                        {previewExercises.map((ex, i) => (
                          <div key={i} className={styles.exerciseItem}>
                            <span className={styles.exerciseOrder}>{ex.order ?? i + 1}</span>
                            <span className={styles.exerciseName}>
                              {ex.customName ?? '(esercizio libreria)'}
                            </span>
                            {ex.durationMinutes != null && (
                              <span className={styles.exerciseSets}>{ex.durationMinutes} min</span>
                            )}
                          </div>
                        ))}
                        {remaining > 0 && (
                          <span className={styles.moreExercises}>
                            +{remaining} altr{remaining === 1 ? 'o' : 'i'}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Footer */}
                    <div className={styles.cardFooter}>
                      <div className={styles.cardMeta}>
                        <span className={styles.metaItem}>
                          <Box size={13} strokeWidth={2.5} />
                          {exercises.length} eserciz{exercises.length === 1 ? 'io' : 'i'}
                        </span>
                        {block.defaultDurationMinutes != null && (
                          <span className={styles.metaItem}>
                            <Clock size={13} strokeWidth={2.5} />~{block.defaultDurationMinutes} min
                          </span>
                        )}
                        {block.hasTacticalSchema && block.tacticalSchemaId && (
                          <button
                            type="button"
                            className={styles.metaItem}
                            onClick={async (e) => {
                              e.stopPropagation();
                              const res = await api.tacticalSchemas.one(block.tacticalSchemaId!);
                              if (res.data) {
                                setPreviewOpen({
                                  snapshot: {
                                    mode: res.data.mode,
                                    fieldConfig: res.data.fieldConfig,
                                    layers: res.data.layers,
                                  },
                                  blockName: block.name,
                                });
                              }
                            }}
                            style={{
                              cursor: 'pointer',
                              border: 'none',
                              background: 'transparent',
                              padding: 0,
                              font: 'inherit',
                              color: 'inherit',
                            }}
                          >
                            🎨 Lavagna
                          </button>
                        )}
                      </div>
                      {block.tags?.length > 0 && (
                        <div className={styles.cardTags}>
                          {block.tags.slice(0, 3).map((tag) => (
                            <span key={tag} className={styles.tag}>
                              {tag}
                            </span>
                          ))}
                          {block.tags.length > 3 && (
                            <span className={styles.tag}>+{block.tags.length - 3}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={styles.paginationRow}>
              <span className={styles.paginationInfo}>
                Pagina {page} di {totalPages}
              </span>
              <div className={styles.paginationButtons}>
                <button
                  className={styles.pageBtn}
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft size={16} />
                </button>
                <span className={styles.pageCounter}>
                  {page} / {totalPages}
                </span>
                <button
                  className={styles.pageBtn}
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <FieldBlockBuilderModal
        isOpen={builderOpen}
        block={editingBlock ?? undefined}
        onClose={handleClose}
      />

      {previewOpen && (
        <GenericModalV2
          isOpen
          onClose={() => setPreviewOpen(null)}
          size="xl"
          Header={<p style={{ padding: 8, fontWeight: 600 }}>Lavagna · {previewOpen.blockName}</p>}
        >
          <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
            <Suspense fallback={<LeopartLoader />}>
              <TacticalBoardViewer snapshot={previewOpen.snapshot} />
            </Suspense>
          </div>
        </GenericModalV2>
      )}
    </div>
  );
};

export default FieldBlocksTab;
